import { Injectable, signal } from '@angular/core';
import { PrepSound } from './kitchen-settings.service';

interface Note {
  frequency: number;
  start: number;
  duration: number;
  type: OscillatorType;
}

const PRESETS: Record<PrepSound, Note[]> = {
  [PrepSound.Bell]: [
    { frequency: 880, start: 0, duration: 1.4, type: 'sine' },
    { frequency: 1760, start: 0, duration: 0.7, type: 'sine' },
    { frequency: 2640, start: 0, duration: 0.3, type: 'sine' },
  ],
  [PrepSound.Beep]: [
    { frequency: 1000, start: 0, duration: 0.15, type: 'square' },
    { frequency: 1000, start: 0.25, duration: 0.15, type: 'square' },
  ],
  [PrepSound.Chime]: [
    { frequency: 659.25, start: 0, duration: 0.6, type: 'sine' },
    { frequency: 783.99, start: 0.18, duration: 0.6, type: 'sine' },
    { frequency: 1046.5, start: 0.36, duration: 0.9, type: 'sine' },
  ],
  [PrepSound.Alarm]: [0, 1, 2, 3, 4, 5].map((step) => ({
    frequency: step % 2 === 0 ? 960 : 720,
    start: step * 0.14,
    duration: 0.13,
    type: 'triangle' as OscillatorType,
  })),
};

// Synthesized rather than shipped as audio files: a square or triangle wave
// sounds much louder than a sine at the same gain, so the peak stays well
// below 1 to keep the loudest preset from clipping at full volume.
const MAX_PEAK_GAIN = 0.4;
const MUTED_STORAGE_KEY = 'yz.prepSound.muted';

@Injectable({ providedIn: 'root' })
export class KitchenSoundService {
  readonly muted = signal(this.readMuted());
  readonly locked = signal(false);

  private context: AudioContext | null = null;

  prepare(): void {
    const context = this.ensureContext();
    if (!context || context.state !== 'suspended') {
      return;
    }
    this.locked.set(true);
    // Browsers keep audio suspended until a user gesture: any touch on the page unlocks it.
    const unlockOnGesture = () => {
      this.unlock();
      document.removeEventListener('pointerdown', unlockOnGesture);
      document.removeEventListener('keydown', unlockOnGesture);
    };
    document.addEventListener('pointerdown', unlockOnGesture);
    document.addEventListener('keydown', unlockOnGesture);
  }

  unlock(): void {
    const context = this.ensureContext();
    if (!context) {
      return;
    }
    void context
      .resume()
      .then(() => this.locked.set(context.state === 'suspended'))
      .catch(() => undefined);
  }

  play(sound: PrepSound, volume: number): void {
    const context = this.ensureContext();
    const peak = (Math.min(Math.max(volume, 0), 100) / 100) * MAX_PEAK_GAIN;
    if (!context || peak <= 0) {
      return;
    }
    if (context.state === 'suspended') {
      void context.resume().catch(() => undefined);
    }
    const now = context.currentTime;
    for (const note of PRESETS[sound] ?? PRESETS[PrepSound.Bell]) {
      const start = now + note.start;
      const end = start + note.duration;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = note.type;
      oscillator.frequency.setValueAtTime(note.frequency, start);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(peak, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(start);
      oscillator.stop(end + 0.05);
    }
  }

  toggleMuted(): void {
    const next = !this.muted();
    this.muted.set(next);
    try {
      localStorage.setItem(MUTED_STORAGE_KEY, String(next));
    } catch {
      // Private browsing: the choice still holds for this page load.
    }
    if (!next) {
      this.unlock();
    }
  }

  protected createContext(): AudioContext | null {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    return Ctor ? new Ctor() : null;
  }

  private ensureContext(): AudioContext | null {
    if (!this.context) {
      try {
        this.context = this.createContext();
      } catch {
        this.context = null;
      }
    }
    return this.context;
  }

  private readMuted(): boolean {
    try {
      return localStorage.getItem(MUTED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }
}
